import {
  workflowTableRowStateFor,
  type InboxWorkflowRowControls,
  type InboxWorkflowRowOutcomeMap,
  type InboxWorkflowState,
  type InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/inbox-workflow/contract"

export type InboxCustomStepRowPresentation = {
  actionCaseId: string
  rowNumber: number
  disabled: boolean
  selectable: boolean
  muted: boolean
  disabledReason: string | null
  mutedReason: string | null
  removedEarlier: boolean
  removedCurrent: boolean
  isResolved: boolean
  isOpen: boolean
  isCompatible: boolean
  statusLabel: string
  statusDetail: string | null
}

export const buildInboxCustomStepRowPresentations = ({
  workflow,
  step,
  rowOutcomes = {},
  rowControls = null,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  rowOutcomes?: InboxWorkflowRowOutcomeMap
  rowControls?: InboxWorkflowRowControls | null
}): Record<string, InboxCustomStepRowPresentation> =>
  Object.fromEntries(
    workflow.items.flatMap((item, index) => {
      const actionCaseId = String(item.action_case.id ?? "").trim()

      if (!actionCaseId) return []

      const rowState = workflowTableRowStateFor({
        item,
        step,
        outcome: rowOutcomes[actionCaseId] ?? null,
      })
      const removedAt = rowControls?.removedStepIndexFor(actionCaseId) ?? null
      const removedEarlier =
        typeof removedAt === "number" &&
        rowControls !== null &&
        removedAt < rowControls.activeStepIndex
      const removedCurrent =
        typeof removedAt === "number" &&
        rowControls !== null &&
        removedAt === rowControls.activeStepIndex
      const removed = removedEarlier || removedCurrent
      const disabled = rowState.disabled || removed
      const disabledReason = rowState.disabled
        ? (rowState.statusDetail ?? rowState.statusLabel)
        : removed
          ? "Diese Zeile wird in diesem Schritt ausgelassen."
          : null

      return [
        [
          actionCaseId,
          {
            actionCaseId,
            rowNumber: index + 1,
            disabled,
            selectable: !disabled,
            muted: disabled,
            disabledReason,
            mutedReason: removed
              ? "Diese Zeile wird in diesem Schritt ausgelassen."
              : disabledReason,
            removedEarlier,
            removedCurrent,
            isResolved: rowState.isResolved,
            isOpen: rowState.isOpen,
            isCompatible: rowState.isCompatible,
            statusLabel: rowState.statusLabel,
            statusDetail: rowState.statusDetail,
          } satisfies InboxCustomStepRowPresentation,
        ],
      ]
    }),
  )
